import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useGameStore } from "@/store/gameStore";
import { GameConfigParams } from "@/store/types";
import { createConfiguredGame } from "@/store/gameConfig";
import { gameDocToGame, gameToConfigInput } from "@/hooks/convex/gameMapping";

export function useConvexConfig() {
  const configGame = useGameStore((state) => state.configGame);
  const unShareGame = useGameStore((state) => state.unShareGame);
  const setGame = useGameStore((state) => state.setGame);
  const setSyncStatus = useGameStore((state) => state.setSyncStatus);
  const configGameMutation = useMutation(api.game.config);

  const configGameWithSync = async (
    params: GameConfigParams,
  ): Promise<void> => {
    const currentGame = useGameStore.getState().game;
    if (currentGame.syncStatus === "syncing") return;
    if (!currentGame?.slug) {
      configGame(params);
      return;
    }

    const proposedGame = createConfiguredGame(currentGame, params);
    const { slug, roundId } = currentGame;
    if (!roundId) return;
    const isStillOnRequestedRound = () => {
      const latestGame = useGameStore.getState().game;
      return latestGame.slug === slug && latestGame.roundId === roundId;
    };
    setSyncStatus("syncing");

    try {
      const result = await configGameMutation({
        slug,
        expectedRoundId: roundId,
        game: gameToConfigInput(proposedGame),
      });

      if (result.status === "ok") {
        const latestGame = useGameStore.getState().game;
        const stillOnRequestedRound =
          latestGame.slug === slug &&
          (latestGame.roundId === roundId ||
            latestGame.roundId === result.game.roundId);
        if (stillOnRequestedRound) setGame(gameDocToGame(result.game));
      } else if (result.status === "not_found") {
        if (isStillOnRequestedRound()) {
          unShareGame();
          configGame(params);
        }
      } else if (isStillOnRequestedRound()) {
        setSyncStatus(result.status === "stale" ? "synced" : "failed");
      }
    } catch (error) {
      console.error("Config error:", error);
      if (isStillOnRequestedRound()) setSyncStatus("failed");
    }
  };

  return { configGameWithSync };
}
